import React, { useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import type { BoitageDocumentKind, BoitageWindow } from '../../../../@types/Boitage';
import type { RootState } from '../../../../store/store';
import { getCompanyBoitageWindowDocument, getCompanyBoitageWindows, postCompanyBoitageWindowDocument } from '../../../../utils/api';
import { downloadCommonFiles } from '../../../../utils/fileDownloadUtils';
import { JWTAuthorization, JWTHeader } from '../../../../utils/JWTHeader';
import { ROUTES } from '../../../../utils/routes';
import tailwindColors from '../../../../utils/tailwindColors';
import FileUpload from '../../../common/FileUpload/FileUpload.module';
import { useFileUpload } from '../../../common/FileUpload/useFileUpload';
import { showToast } from '../../../common/Notification';

const SUPPORTED_KINDS: { kind: BoitageDocumentKind; label: string; badge: { initials: string; bg: string; text: string } }[] = [
  { kind: 'stop-pub', label: 'Modèle stop pub', badge: { initials: 'SP', bg: 'bg-red-100', text: 'text-red-600' } },
  { kind: 'sans-stop-pub', label: 'Modèle sans stop pub', badge: { initials: 'SSP', bg: 'bg-emerald-100', text: 'text-emerald-700' } },
];

type ModelCardProps = {
  label: string;
  badge: { initials: string; bg: string; text: string };
  kind: BoitageDocumentKind;
  year: number;
  eventIndex: 1 | 2;
  hasDocument: boolean;
  onUploaded: () => void;
};

const ModelCard: React.FC<ModelCardProps> = ({ label, badge, kind, year, eventIndex, hasDocument, onUploaded }) => {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);

  const uploadBoitageFile = async(file: File): Promise<boolean> => {
    if (!company?.id || !jwt) { return false; }
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await fetch(postCompanyBoitageWindowDocument(company.id, year, eventIndex, kind), {
        method: 'POST',
        headers: { Authorization: JWTAuthorization(jwt) },
        body: formData,
      });
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      showToast('success', `${label} enregistré.`);
      setIsReplacing(false);
      setShowPreview(false);
      setPreviewUrl(null);
      onUploaded();
      return true;
    } catch (e) {
      console.error(`Error uploading boitage document (${kind}, ${year}:${eventIndex}):`, e);
      showToast('error', 'Erreur lors du téléchargement du document.');
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  const fileUpload = useFileUpload({
    onFileSelect: uploadBoitageFile,
    onFileDelete: () => onUploaded(),
    acceptedFileTypes: '.pdf,.doc,.docx',
    maxFileSize: 10,
    currentFile: null,
  });

  const loadPreview = async() => {
    if (!company?.id || !jwt) { return; }
    try {
      const resp = await fetch(getCompanyBoitageWindowDocument(company.id, year, eventIndex, kind), {
        headers: { Authorization: JWTAuthorization(jwt) },
      });
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const blob = await resp.blob();
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (e) {
      console.error(`Error loading preview (${kind}):`, e);
      showToast('error', 'Impossible de charger l\'aperçu du PDF.');
    }
  };

  const handleDownload = async() => {
    if (!company?.id || !jwt) { return; }
    try {
      const resp = await fetch(getCompanyBoitageWindowDocument(company.id, year, eventIndex, kind), {
        headers: { Authorization: JWTAuthorization(jwt) },
      });
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${label.replaceAll(/\s+/g, '-')}-${year}-${eventIndex}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(`Error downloading boitage document (${kind}):`, e);
      showToast('error', 'Erreur lors du téléchargement du modèle.');
    }
  };

  return (
    <div className={`rounded-lg p-4 transition-all ${isUploading ? 'border-2 border-blue-300 bg-blue-50' : hasDocument ? 'border-2 border-green-200 bg-green-50' : 'border-2 border-dashed border-slate-200 bg-slate-50'}`}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${badge.bg} ${badge.text}`}>{badge.initials}</span>
          <div>
            <div className="text-sm font-semibold text-slate-800">{label}</div>
            <div className="text-xs text-slate-500">
              {isUploading ? 'Téléchargement en cours…' : hasDocument ? 'Modèle configuré' : 'Modèle non configuré'}
            </div>
          </div>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-medium ${isUploading ? 'bg-blue-500 text-white shadow-sm' : hasDocument ? 'bg-green-500 text-white shadow-sm' : 'bg-slate-300 text-slate-600'}`}>
          {isUploading ? <><span role="img" aria-label="loader">⏳</span> En cours</> : hasDocument ? <><span role="img" aria-label="check mark">✓</span> Présent</> : <><span role="img" aria-label="circle">○</span> Manquant</>}
        </span>
      </div>

      {(isReplacing || (!hasDocument && !isUploading)) && (
        <div className="mt-2 space-y-2">
          <FileUpload
            onFileSelect={fileUpload.handleFiles}
            onFileDelete={fileUpload.removeFile}
            acceptedFileTypes=".pdf,.doc,.docx"
            maxFileSize={10}
            currentFile={null}
          />
          {isReplacing && (
            <div className="flex justify-end">
              <button
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
                onClick={() => setIsReplacing(false)}
                disabled={isUploading}
              >
                Annuler
              </button>
            </div>
          )}
        </div>
      )}

      {hasDocument && !isReplacing && (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              className="rounded-lg bg-rgpd-blue px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-rgpd-blue-dark"
              onClick={handleDownload}
            >
              ⬇ Télécharger
            </button>
            <button
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
              onClick={async() => {
                const next = !showPreview;
                setShowPreview(next);
                if (next && !previewUrl) {
                  await loadPreview();
                }
              }}
            >
              {showPreview ? <><span role="img" aria-label="emoji">👁</span>️ Masquer l'aperçu</> : <><span role="img" aria-label="emoji">👁</span>️ Voir l'aperçu</>}
            </button>
            <button
              className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-50"
              onClick={() => {
                if (confirm('Êtes-vous sûr de vouloir remplacer le modèle actuel ?')) {
                  setShowPreview(false);
                  setIsReplacing(true);
                }
              }}
            >
              <span role="img" aria-label="refresh">🔄</span> Remplacer
            </button>
          </div>

          {showPreview && (
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 bg-slate-100 px-3 py-2">
                <p className="text-xs font-medium text-slate-700">Aperçu du document</p>
              </div>
              <div className="p-2">
                <object data={previewUrl || ''} type="application/pdf" className="w-full rounded" style={{ height: 420 }}>
                  <div className="p-6 text-center text-xs text-slate-500">
                    Aperçu disponible uniquement pour les fichiers PDF. Utilisez le bouton Télécharger pour les autres formats.
                  </div>
                </object>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const buildWindowLabel = (window: BoitageWindow): string => {
  const planned = new Date(`${window.plannedDate}T00:00:00`);
  const year = planned.getFullYear();
  return `Boîtage ${window.eventIndex} — ${window.plannedLabel} ${year}`;
};

const BoitageDocumentsPanel: React.FC = () => {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);

  const [windows, setWindows] = useState<BoitageWindow[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchWindows = async() => {
    if (!company?.id || !jwt) { return; }
    setIsLoading(true);
    try {
      const resp = await fetch(getCompanyBoitageWindows(company.id), JWTHeader(jwt));
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const data: BoitageWindow[] = await resp.json();
      setWindows(data);
    } catch (e) {
      console.error('Error fetching boitage windows :', e);
      setWindows([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchWindows(); }, [company?.id, jwt]);

  const skeletonRows = useMemo(() => Array.from({ length: 2 }, (_, i) => i), []);

  return (
    <div className="mb-6 rounded-lg border bg-white shadow-sm">
      <div className="p-5">
        <div className="mb-5 flex items-center justify-between border-b border-slate-200 pb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Modèles de boîtage</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Téléchargez un modèle d'exemple ci-dessous, puis déposez votre version définitive pour chaque date planifiée
            </p>
          </div>
          <div className="flex gap-2">
            <button
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-50"
              onClick={() => downloadCommonFiles.boitageV1()}
            >
              <span role="img" aria-label="document">📄</span> Modèle V1
            </button>
            <button
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-50"
              onClick={() => downloadCommonFiles.boitageV2()}
            >
              <span role="img" aria-label="document">📄</span> Modèle V2
            </button>
            <button
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-50"
              onClick={() => downloadCommonFiles.boitageV3()}
            >
              <span role="img" aria-label="document">📄</span> Modèle V3
            </button>
          </div>
        </div>

        {isLoading && (
          <div className="space-y-4">
            {skeletonRows.map((i) => (
              <Skeleton key={i} height={160} borderRadius={8} baseColor="#e2e8f0" highlightColor={`${tailwindColors.theme.extend.colors['rgpd-blue']}20`} />
            ))}
          </div>
        )}

        {!isLoading && windows && windows.length === 0 && (
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
            <p className="text-sm font-semibold text-slate-700">Aucune fenêtre de boîtage planifiée</p>
            <p className="mt-1 text-xs text-slate-500">
              Activez et positionnez vos boîtages dans le planning pour pouvoir y associer des modèles.
            </p>
            <Link
              to={`${ROUTES.NEW_DASHBOARD_PARAMETRAGE}?tab=planning`}
              className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-md border border-rgpd-blue px-3 py-1.5 text-xs font-semibold text-rgpd-blue transition-colors hover:bg-rgpd-blue hover:text-white"
            >
              Ouvrir le planning
            </Link>
          </div>
        )}

        {!isLoading && windows && windows.length > 0 && (
          <div className="space-y-6">
            {windows.map((w) => (
              <section key={`${w.year}-${w.eventIndex}`} className="rounded-xl border border-slate-200 bg-slate-50/50 p-4">
                <header className="mb-3 flex items-baseline justify-between">
                  <h3 className="text-base font-bold text-slate-800">{buildWindowLabel(w)}</h3>
                  <span className="text-xs font-medium text-slate-500">
                    Documents prévus : {w.docsPlanned.toLocaleString('fr-FR')}
                  </span>
                </header>
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  {SUPPORTED_KINDS.map(({ kind, label, badge }) => (
                    <ModelCard
                      key={kind}
                      label={label}
                      badge={badge}
                      kind={kind}
                      year={w.year}
                      eventIndex={w.eventIndex}
                      hasDocument={Boolean(w.documents?.[kind])}
                      onUploaded={fetchWindows}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BoitageDocumentsPanel;
